import type { IRect } from '@nova/types';

import { WebglCanvasCtx } from './models/webgl-canvas-ctx.interface';

/**
 * [Client-side only] This class relies on the dom to create a '2D' canvas to perform it's data conversion.
 */
export class ClientTextureUtils {
  // Stateful functionality -----------------------------

  private static canvas?: HTMLCanvasElement;
  private static ctx2D?: CanvasRenderingContext2D;

  private static CreateCanvas2DCached(width: number, height: number) {
    if (!!this.canvas && !!this.ctx2D) {
      this.ClearCanvas();
      this.ResizeCanvas(width, height);
      return;
    }

    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw new Error(`Could not create canvas 2d context`);
    }

    this.canvas = canvas;
    this.ctx2D = ctx;

    this.ResizeCanvas(width, height);
  }

  public static CreateCanvasWebGL(canvasWidth: number, canvasHeight: number): WebglCanvasCtx {
    const gl = ClientTextureUtils.tryCreateWebGL(canvasWidth, canvasHeight);

    if (!gl) {
      throw new Error('WebGL rendering context not available.');
    }

    return { canvas: gl.canvas as HTMLCanvasElement, gl };
  }

  public static tryCreateWebGL(canvasWidth: number, canvasHeight: number): WebGLRenderingContext | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    return canvas.getContext('webgl', {
      preserveDrawingBuffer: false,
      premultipliedAlpha: false
    });
  }

  private static ResizeCanvas(width: number, height: number): void {
    if (!this.canvas) { return; }

    this.canvas.width = width;
    this.canvas.height = height;
  }

  private static ClearCanvas(): void {
    if (!this.canvas) { return; }

    this.ctx2D?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * It's important to use the returned ImageData width and height after acquiring it here,
   * as it's based on the HTML image's intrinsic dimensions, not those altered by css/styling.
   * @param image
   * @returns
   */
  public static GetImageDataFromImage(image: HTMLImageElement): ImageData {
    this.CreateCanvas2DCached(image.naturalWidth, image.naturalHeight);

    if (!this.ctx2D) { throw new Error(`Context not established to get image data`); }

    this.ctx2D.drawImage(image, 0, 0);
    const imgData: ImageData = this.ctx2D.getImageData(0, 0, image.naturalWidth, image.naturalHeight);

    // console.log(imgData);
    return imgData;
  }

  public static GetImageDataFromImageFrame(image: HTMLImageElement, frame: IRect): ImageData {
    this.CreateCanvas2DCached(frame.width, frame.height);

    if (!this.ctx2D) { throw new Error(`Context not established to get image data`); }

    this.ctx2D.drawImage(image, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
    const imgData: ImageData = this.ctx2D.getImageData(0, 0, frame.width, frame.height);

    return imgData;
  }

  /**
   * Draws the supplied frame information and returns a newly created HTML image element from it.
   * TODO: Handle rotations
   * @param image
   * @param frame
   * @returns
   */
  public static GetImageFromImageFrame(image: HTMLImageElement, frame: IRect): HTMLImageElement {
    this.CreateCanvas2DCached(frame.width, frame.height);
    if (!this.canvas || !this.ctx2D) { throw new Error(`Context not established to get image data`); }

    this.ctx2D.drawImage(image, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
    return this.GetImageFromCanvas(this.canvas, frame.width, frame.height);
  }

  public static SetDataUrlFromImageData(imageData: ImageData, image: HTMLImageElement): void {
    // * Get ImageData object by loading image to it's own canvas
    this.CreateCanvas2DCached(imageData.width, imageData.height);

    if (!this.canvas || !this.ctx2D) { throw new Error(`Canvas or Context not established to get image element`); }

    this.ctx2D.putImageData(imageData, 0, 0);

    image.src = this.canvas.toDataURL();
    // ! This sets the -attributes-, not the css, making them very difficult to override.
    // image.width = imageData.width;
    // image.height = imageData.height;
  }

  public static GetDataUrlFromImageData(imageData: ImageData): string {
    // * Get ImageData object by loading image to it's own canvas
    this.CreateCanvas2DCached(imageData.width, imageData.height);
    if (!this.canvas || !this.ctx2D) { throw new Error(`Canvas or Context not established to get image element`); }

    this.ctx2D.putImageData(imageData, 0, 0);
    return this.canvas.toDataURL();
  }

  public static GetImageFromImageData(imageData: ImageData): HTMLImageElement {
    // * Get ImageData object by loading image to it's own canvas
    this.CreateCanvas2DCached(imageData.width, imageData.height);

    if (!this.canvas || !this.ctx2D) { throw new Error(`Canvas or Context not established to get image element`); }

    this.ctx2D.putImageData(imageData, 0, 0);
    return this.GetImageFromCanvas(this.canvas, imageData.width, imageData.height);
  }

  public static GetImageDataFromPixels(pixelData: Uint8Array, width: number, height: number): ImageData {
    this.CreateCanvas2DCached(width, height);

    if (!this.canvas || !this.ctx2D) { throw new Error(`Canvas or Context not established to get image data`); }

    // flip the image
    const bytesPerRow = this.canvas.width * 4;
    const temp = new Uint8Array(bytesPerRow);
    for (let y = 0; y < this.canvas.height / 2; y += 1) {
      const topOffset = y * bytesPerRow;
      const bottomOffset = (this.canvas.height - y - 1) * bytesPerRow;
      temp.set(pixelData.subarray(topOffset, topOffset + bytesPerRow));
      pixelData.copyWithin(topOffset, bottomOffset, bottomOffset + bytesPerRow);
      pixelData.set(temp, bottomOffset);
    }

    const imgData: ImageData = this.ctx2D.createImageData(this.canvas.width, this.canvas.height);
    imgData.data.set(pixelData);

    return imgData;
  }

  public static DisplayCavas(): void {
    if (!this.canvas) { return; }

    document.body.appendChild(this.canvas);
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.right = '0';
  }

  public static DestroyCanvas(): void {
    if (!this.canvas) { return; }

    this.canvas.remove();
    this.canvas = undefined;
    this.ctx2D = undefined;
  }

  // Stateless functionality -----------------------------

  public static async FetchImage(path: string, filename: string): Promise<HTMLImageElement> {
    const image: HTMLImageElement = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => { resolve(img); };
      img.onerror = reject;
      img.src = `${path}${filename}`;
    });

    // console.log(image);
    return image;
  }

  public static GetImageDataFromPixelsWithCtx2d(pixelData: Uint8Array, ctx2d: CanvasRenderingContext2D, width: number, height: number): ImageData {
    // flip the image
    const bytesPerRow = width * 4;
    const temp = new Uint8Array(bytesPerRow);
    for (let y = 0; y < height / 2; y += 1) {
      const topOffset = y * bytesPerRow;
      const bottomOffset = (height - y - 1) * bytesPerRow;
      temp.set(pixelData.subarray(topOffset, topOffset + bytesPerRow));
      pixelData.copyWithin(topOffset, bottomOffset, bottomOffset + bytesPerRow);
      pixelData.set(temp, bottomOffset);
    }

    const imgData: ImageData = ctx2d.createImageData(width, height);
    imgData.data.set(pixelData);

    return imgData;
  }

  public static ResizeCanvasToImg(canvas: HTMLCanvasElement, img: HTMLImageElement): void {
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.style.width = `${img.naturalWidth}px`;
    canvas.style.height = `${img.naturalHeight}px`;
  }

  // Local/helper functions -----------------------------

  private static GetImageFromCanvas(canvas: HTMLCanvasElement, width?: number, height?: number): HTMLImageElement {
    const image = new Image();
    image.src = canvas.toDataURL();

    // ! This sets the -attributes-, not the css, making them very difficult to override.
    // width && (image.width = width);
    // height && (image.height = height);

    return image;
  }
}
